import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Search, Upload, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { STATUS_TONES, usePortal } from "./PortalContext";
import { MessageTagButton } from "./MessageTagButton";

const TABS = [["open", "Open"], ["due_soon", "Due soon"], ["awaiting_consent", "Awaiting consent"], ["completed", "Completed"], ["all", "All"]];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ status, testId }) => <em className={`cp-pill tone-${STATUS_TONES[status] || "neutral"}`} data-testid={testId}>{status}</em>;

const Timing = ({ row, onReport, onConsent }) => {
  const t = row.timing || {};
  if (t.kind === "report") return <button type="button" className="cp-text-link" data-testid={`portal-view-report-${row.id}`} onClick={(e) => { e.stopPropagation(); onReport(row); }}>View report</button>;
  if (t.kind === "consent") return <button type="button" className="cp-text-link" data-testid={`portal-upload-consent-${row.id}`} onClick={(e) => { e.stopPropagation(); onConsent(row); }}>Upload consent</button>;
  if (t.kind === "revised") return <span className="cp-timing-revised" data-testid={`portal-timing-${row.id}`}>Revised date</span>;
  if (t.kind === "information") return <span className="cp-timing-revised" data-testid={`portal-timing-${row.id}`}>Information needed</span>;
  return <span className="cp-timing" data-testid={`portal-timing-${row.id}`}>{t.label}</span>;
};

const ConsentUpload = ({ row, onDone }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const portal = usePortal();
  async function upload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      await api.post(`/client-portal/evaluations/${row.id}/consent`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setError("");
      await onDone();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }
  if (portal.preview) return <p className="cp-inline-note">Consent uploads are disabled in preview.</p>;
  return (
    <label className="cp-upload" data-testid={`portal-consent-upload-${row.id}`}>
      <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid={`portal-consent-input-${row.id}`} onChange={upload} disabled={busy} />
      <span className="cp-btn cp-btn-primary"><Upload size={14} /> {busy ? "Uploading…" : "Upload signed consent"}</span>
      {error && <small className="cp-error">{error}</small>}
    </label>
  );
};

const DetailPanel = ({ id, onClose, onChanged, wantsConsent }) => {
  const portal = usePortal();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await portal.get(`/evaluations/${id}`);
      setDetail(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [portal, id]);
  useEffect(() => { load(); }, [load]);
  const reportUrl = `${BACKEND}/api/client-portal/evaluations/${id}/report${portal.previewClientId ? `?client_id=${portal.previewClientId}` : ""}`;
  return (
    <aside className="cp-detail" data-testid="portal-evaluation-detail">
      <button type="button" className="cp-icon-button cp-detail-close" aria-label="Close" data-testid="portal-detail-close" onClick={onClose}><X size={16} /></button>
      {error && <p className="cp-error">{error}</p>}
      {!detail && !error && <p className="cp-muted">Loading…</p>}
      {detail && (
        <>
          <p className="cp-eyebrow">Evaluation detail</p>
          <h2 data-testid="portal-detail-title">{detail.student} · {detail.evaluation_type}</h2>
          <dl className="cp-facts">
            <div><dt>Provider</dt><dd data-testid="portal-detail-provider">{detail.provider}</dd></div>
            <div><dt>Status</dt><dd><StatusPill status={detail.status} testId="portal-detail-status" /></dd></div>
            <div><dt>Due</dt><dd data-testid="portal-detail-due">{detail.meeting_label ? `Meeting ${detail.meeting_label} · Report due ${detail.due_label || "—"}` : (detail.due_label || "—")}{detail.timing?.kind === "days" ? ` · ${detail.timing.label}` : ""}</dd></div>
            {detail.grade && <div><dt>Grade</dt><dd>{detail.grade}</dd></div>}
            <div><dt>Requested</dt><dd>{detail.requested_on}</dd></div>
          </dl>
          {detail.timing?.kind === "revised" && (
            <div className="cp-note-box" data-testid="portal-detail-revision">
              <b>Date revised{detail.timing.revised_from && detail.timing.revised_from !== detail.due_label ? ` from ${detail.timing.revised_from}` : ""}.</b> {detail.timing.note}
            </div>
          )}
          {detail.needs_information && <div className="cp-note-box" data-testid="portal-detail-information">{detail.client_message}</div>}
          <h3>Status history</h3>
          <ol className="cp-timeline" data-testid="portal-detail-timeline">
            {detail.timeline.map((step) => (
              <li key={step.label} className={step.done ? "is-done" : step.current ? "is-current" : "is-upcoming"}>
                <span>{step.label}</span>
                {step.date && <small>{step.date}</small>}
              </li>
            ))}
          </ol>
          {detail.needs_consent && (wantsConsent || true) && <ConsentUpload row={detail} onDone={async () => { await load(); await onChanged(); }} />}
          {detail.report_ready && (
            <a className="cp-btn cp-btn-primary cp-btn-block" href={reportUrl} target="_blank" rel="noreferrer" data-testid="portal-detail-report"><FileText size={14} /> View report</a>
          )}
          <MessageTagButton evaluationId={detail.id} label="Message TAG about this request" className="cp-btn cp-btn-outline cp-btn-block" testId="portal-detail-message" />
          {detail.messages?.length > 0 && (
            <ul className="cp-messages" data-testid="portal-detail-messages">
              {detail.messages.map((m) => <li key={m.id}><small>{new Date(m.at).toLocaleString()} · {m.from}</small><p>{m.body}</p></li>)}
            </ul>
          )}
        </>
      )}
    </aside>
  );
};

export default function PortalEvaluations() {
  const portal = usePortal();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "open";
  const selected = params.get("open");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await portal.get("/evaluations", { tab, q: query });
      setData(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [portal, tab, query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (selected && window.matchMedia("(max-width: 1080px)").matches) {
      requestAnimationFrame(() => document.querySelector("[data-testid='portal-evaluation-detail']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [selected]);

  const select = (id, extra = {}) => {
    const next = new URLSearchParams(params);
    if (id) next.set("open", id); else next.delete("open");
    next.delete("consent");
    Object.entries(extra).forEach(([k, v]) => next.set(k, v));
    setParams(next);
  };
  const openReport = (row) => window.open(`${BACKEND}/api/client-portal/evaluations/${row.id}/report${portal.previewClientId ? `?client_id=${portal.previewClientId}` : ""}`, "_blank", "noopener");

  return (
    <div className="cp-page" data-testid="portal-evaluations">
      <h1 className="cp-h1">Evaluation tracker</h1>
      <p className="cp-lede">Every evaluation you have requested, who is assigned, and when it is due.</p>
      <div className="cp-toolbar">
        <div className="cp-chips" role="tablist">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={`cp-chip ${tab === key ? "is-active" : ""}`} data-testid={`portal-filter-${key}`} onClick={() => { const next = new URLSearchParams(params); next.set("tab", key); setParams(next); }}>
              {label}{data?.counts ? ` · ${data.counts[key]}` : ""}
            </button>
          ))}
        </div>
        <label className="cp-search"><Search size={14} /><input value={query} placeholder="Search initials" data-testid="portal-search" onChange={(e) => setQuery(e.target.value)} /></label>
      </div>
      {error && <p className="cp-error">{error}</p>}
      <div className={`cp-tracker ${selected ? "has-detail" : ""}`}>
        <section className="cp-table-card">
          <div className="cp-table-scroll">
          <table className="cp-table" data-testid="portal-evaluation-table">
            <thead><tr><th>Student</th><th>Evaluation</th><th>Provider</th><th>Status</th><th>Due</th><th>Timing</th></tr></thead>
            <tbody>
              {(data?.rows || []).map((row) => (
                <tr key={row.id} className={selected === row.id ? "is-selected" : ""} data-testid={`portal-row-${row.id}`} onClick={() => select(row.id)}>
                  <td><b>{row.student}</b></td>
                  <td>{row.evaluation_type}</td>
                  <td>{row.provider}</td>
                  <td><StatusPill status={row.status} testId={`portal-status-${row.id}`} /></td>
                  <td data-testid={`portal-due-${row.id}`}>{row.meeting_label ? <span className="cp-due-meeting">Meeting {row.meeting_label} · Report due {row.due_label || "—"}</span> : (row.due_label || "—")}</td>
                  <td><Timing row={row} onReport={openReport} onConsent={(r) => select(r.id, { consent: "1" })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {data && data.rows.length === 0 && <p className="cp-muted cp-table-empty" data-testid="portal-table-empty">No evaluations match this view.</p>}
          {data && <p className="cp-table-foot" data-testid="portal-table-count">Showing {data.rows.length} of {data.counts.all} evaluations</p>}
        </section>
        {selected && <DetailPanel id={selected} wantsConsent={params.get("consent") === "1"} onClose={() => select(null)} onChanged={load} />}
      </div>
    </div>
  );
}
